import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { protectedResourceMap } from '@commonConfig/msalb2c.config';
import { isNullish } from '@commonHelpers/math-utils';
import type { ProtectedResourceScopes } from '@azure/msal-angular';
import type { AuthenticationResult } from "@azure/msal-browser";
import { MsalB2CService } from "@commonServices/msalb2c.service";
import { Observable, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { apiAccesses } from './msalb2c.config';
;

// TODO Interceptor entfernen wenn https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/3758 gefixt ist und msal Interceptor verwenden
//
// Stand msal-angular 6.2.0: noch nicht gefixt. Der Grund, warum dieser Interceptor existiert,
// besteht unverändert - MsalInterceptor.acquireToken() ruft bei jedem gescheiterten
// acquireTokenSilent acquireTokenInteractively, und zusätzlich auch dann, wenn der stille
// Aufruf erfolgreich war aber kein accessToken lieferte (im Quelltext als "Known issue with
// B2C tenants" kommentiert). Über die Konfiguration ist das nicht abschaltbar:
// MsalInterceptorConfiguration hat nur interactionType (typisiert auf Popup | Redirect),
// protectedResourceMap, authRequest und strictMatching. Das Portal braucht aber Aufrufe, die
// angemeldet mehr liefern und abgemeldet trotzdem nie 401 sind - eine Umleitung ist dort also
// keine Option.
//
// Ein Weg dorthin gibt es trotzdem, falls dieser Interceptor einmal entfallen soll:
// intercept() lässt eine Anfrage unverändert durch, wenn getScopesForEndpoint() nichts findet
// ("If no scopes for endpoint, does not acquire token"), und matchResourcesToEndpoint() liest
// protectedResourceMap.keys() bei jeder Anfrage neu - nicht einmalig beim Erzeugen der
// Konfiguration. Eine Map, die nur befüllt ist, solange isLoggedIn() gilt, ergibt damit exakt
// das gewünschte Verhalten: Token wenn angemeldet, sonst weder Token noch Umleitung.
// Zugegriffen wird ausschließlich über keys() und get(). Offen bleibt dabei nur der Fall
// angemeldet mit gescheitertem stillen Aufruf: der Standard-Interceptor leitet dann um, hier
// wird abgemeldet.
//
// Beim Wechsel unbedingt beachten: msal-angular 6 vergleicht die Schlüssel der
// protectedResourceMap standardmäßig verankert (strictMatching). Die bloßen Basisadressen aus
// apiAccesses treffen damit nichts mehr, es braucht einen Pfad-Platzhalter - siehe
// withPathWildcard in Orca.Ade.CatalogManager.Gui/src/app/msal/msalb2c.config.ts. Der
// startsWith-Vergleich unten ist davon nicht betroffen.
@Injectable({
	providedIn: 'root'
})
export class MsalB2CInterceptor implements HttpInterceptor {
	constructor(
		private msalB2CService: MsalB2CService
	) { }

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (req.url.startsWith('/assets/') || !this.msalB2CService.isLoggedIn()) {
			return next.handle(req);
		}

		const scopes = this.getScopesForEndpoint(req.url);
		if (!scopes || scopes.length === 0) {
			return next.handle(req);
		}

		// Note: For MSA accounts, include openid scope when calling acquireTokenSilent to return idToken
		return from(this.msalB2CService.acquireTokenSilent(scopes as any))
			.pipe(
				catchError(() => from(this.msalB2CService.logout())),
				switchMap((result: AuthenticationResult) => {
					const headers = req.headers
						.set('Authorization', `Bearer ${result.accessToken}`)

					const requestClone = req.clone({ headers });
					return next.handle(requestClone);
				})
			)

	}

	private getScopesForEndpoint(endpoint: string): (string | ProtectedResourceScopes)[] {
		const protectedResourcesArray = Array.from(protectedResourceMap(apiAccesses).keys());
		const keyMatchesEndpointArray = protectedResourcesArray.filter(key => {
			if (!isNullish(key)) {
				return endpoint.startsWith(`${key}/`);
			}

		});
		// process all protected resources and send the first matched resource
		if (keyMatchesEndpointArray.length > 0) {
			const keyForEndpoint = keyMatchesEndpointArray[0];
			if (keyForEndpoint) {
				return protectedResourceMap(apiAccesses).get(keyForEndpoint);
			}
		}

		return null;
	}
}
